import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { RestaurantProvider } from "@/contexts/RestaurantContext";
import { SQLiteProvider } from "@/contexts/SQLiteContext";
import { DataSourceIndicator } from "@/components/dev/DataSourceIndicator";

// Public pages
import HomeRouter from "./pages/HomeRouter";
import Auth from "./pages/Auth";
import ResetPassword from "./pages/ResetPassword";
import Onboarding from "./pages/Onboarding";
import NotFound from "./pages/NotFound";
import PublicMenuPage from "./pages/public/PublicMenuPage";
import PrivacidadePage from "./pages/PrivacidadePage";
import TableMenuPage from "./pages/public/TableMenuPage";
import TabletMenuPage from "./pages/public/TabletMenuPage";

// KDS layout and pages
import KdsLayout from "./layouts/KdsLayout";
import KdsSelectStation from "./pages/kds/KdsSelectStation";
import KdsStationScreen from "./pages/kds/KdsStationScreen";
import KdsExpoScreen from "./pages/kds/KdsExpoScreen";
import KdsDashboardScreen from "./pages/kds/KdsDashboardScreen";

// Dashboard layout and pages
import DashboardLayout from "./layouts/DashboardLayout";
import DashboardHome from "./pages/dashboard/DashboardHome";
import CategoriasPage from "./pages/dashboard/CategoriasPage";
import ProdutosPage from "./pages/dashboard/ProdutosPage";
import PDVPage from "./pages/dashboard/PDVPage";
import PedidosPage from "./pages/dashboard/PedidosPage";
import RelatoriosPage from "./pages/dashboard/RelatoriosPage";
import ConfiguracoesPage from "./pages/dashboard/ConfiguracoesPage";
import FormasPagamentoPage from "./pages/dashboard/FormasPagamentoPage";
import LocaisProducaoPage from "./pages/dashboard/LocaisProducaoPage";
import CaixaPage from "./pages/dashboard/CaixaPage";
import MesasPage from "./pages/dashboard/MesasPage";
import ComandasPage from "./pages/dashboard/ComandasPage";
import EquipePage from "./pages/dashboard/EquipePage";
import ConsumoAbertoPage from "./pages/dashboard/ConsumoAbertoPage";
import LogsPage from "./pages/dashboard/LogsPage";
import EstoquePage from "./pages/dashboard/EstoquePage";
import CMVPage from "./pages/dashboard/CMVPage";
import MargensPage from "./pages/dashboard/MargensPage";
import GarcomPage from "./pages/dashboard/GarcomPage";
import AberturaComandaPage from "./pages/dashboard/AberturaComandaPage";
import RelatorioEntradasPage from "./pages/dashboard/RelatorioEntradasPage";
import ImpressorasPage from "./pages/dashboard/ImpressorasPage";
import TicketMedioPage from "./pages/dashboard/TicketMedioPage";
import CancelamentosPage from "./pages/dashboard/CancelamentosPage";
import AjudaPage from "./pages/dashboard/AjudaPage";
import AdicionaisPage from "./pages/dashboard/AdicionaisPage";
import CombosPage from "./pages/dashboard/CombosPage";
import RelatorioCombosPage from "./pages/dashboard/RelatorioCombosPage";
import AvaliacoesPage from "./pages/dashboard/AvaliacoesPage";
import ClientesPage from "./pages/dashboard/ClientesPage";
import ErrosPage from "./pages/dashboard/ErrosPage";
import DespesasPage from "./pages/dashboard/DespesasPage";
import FornecedoresPage from "./pages/dashboard/FornecedoresPage";
import PlanoContasPage from "./pages/dashboard/PlanoContasPage";
import DREPage from "./pages/dashboard/DREPage";
import CategoriaDespesasPage from "./pages/dashboard/CategoriaDespesasPage";
import FiscalPage from "./pages/dashboard/FiscalPage";
import DeliveryPage from "./pages/dashboard/DeliveryPage";
import RelatorioGrupoPage from "./pages/dashboard/RelatorioGrupoPage";
import RelatorioFiscalEmissoesPage from "./pages/dashboard/RelatorioFiscalEmissoesPage";

// Admin layout and pages
import AdminLayout from "./layouts/AdminLayout";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminRestaurantesPage from "./pages/admin/AdminRestaurantesPage";
import AdminPagamentosPage from "./pages/admin/AdminPagamentosPage";
import AdminUsuariosPage from "./pages/admin/AdminUsuariosPage";
import AdminConfiguracoesPage from "./pages/admin/AdminConfiguracoesPage";
import AdminDocumentacaoPage from "./pages/admin/AdminDocumentacaoPage";
import AdminLeadsPage from "./pages/admin/AdminLeadsPage";
import AdminWhatsAppPage from "./pages/admin/AdminWhatsAppPage";

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <RestaurantProvider>
        <SQLiteProvider>
          <Toaster />
          <Sonner />
          <DataSourceIndicator />
          <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomeRouter />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/privacidade" element={<PrivacidadePage />} />
            
            {/* Public menu routes */}
            <Route path="/menu/:slug" element={<PublicMenuPage />} />
            <Route path="/menu/:slug/mesa/:tableNumber" element={<TableMenuPage />} />
            <Route path="/menu/:slug/tablet" element={<TabletMenuPage />} />

            {/* Dashboard routes */}
            <Route path="/dashboard" element={<DashboardLayout />}>
              <Route index element={<DashboardHome />} />
              <Route path="categorias" element={<CategoriasPage />} />
              <Route path="produtos" element={<ProdutosPage />} />
              <Route path="adicionais" element={<AdicionaisPage />} />
              <Route path="combos" element={<CombosPage />} />
              <Route path="pdv" element={<PDVPage />} />
              <Route path="delivery" element={<DeliveryPage />} />
              <Route path="pedidos" element={<PedidosPage />} />
              <Route path="caixa" element={<CaixaPage />} />
              <Route path="mesas" element={<MesasPage />} />
              <Route path="comandas" element={<ComandasPage />} />
              <Route path="equipe" element={<EquipePage />} />
              <Route path="consumo-aberto" element={<ConsumoAbertoPage />} />
              <Route path="estoque" element={<EstoquePage />} />
              <Route path="logs" element={<LogsPage />} />
              <Route path="despesas" element={<DespesasPage />} />
              <Route path="categorias-despesa" element={<CategoriaDespesasPage />} />
              <Route path="fornecedores" element={<FornecedoresPage />} />
              <Route path="plano-contas" element={<PlanoContasPage />} />
              <Route path="dre" element={<DREPage />} />
              <Route path="relatorios" element={<RelatoriosPage />} />
              <Route path="relatorio-combos" element={<RelatorioCombosPage />} />
              <Route path="cmv" element={<CMVPage />} />
              <Route path="margens" element={<MargensPage />} />
              <Route path="garcom" element={<GarcomPage />} />
              <Route path="abertura-comandas" element={<AberturaComandaPage />} />
              <Route path="relatorio-entradas" element={<RelatorioEntradasPage />} />
              <Route path="formas-pagamento" element={<FormasPagamentoPage />} />
              <Route path="locais-producao" element={<LocaisProducaoPage />} />
              <Route path="impressoras" element={<ImpressorasPage />} />
              <Route path="ticket-medio" element={<TicketMedioPage />} />
              <Route path="cancelamentos" element={<CancelamentosPage />} />
              <Route path="avaliacoes" element={<AvaliacoesPage />} />
              <Route path="clientes" element={<ClientesPage />} />
              <Route path="ajuda" element={<AjudaPage />} />
              <Route path="erros" element={<ErrosPage />} />
              <Route path="fiscal" element={<FiscalPage />} />
              <Route path="relatorio-grupo" element={<RelatorioGrupoPage />} />
              <Route path="relatorio-fiscal-emissoes" element={<RelatorioFiscalEmissoesPage />} />
              <Route path="configuracoes" element={<ConfiguracoesPage />} />
            </Route>

            {/* Admin routes (Super Admin only) */}
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="restaurantes" element={<AdminRestaurantesPage />} />
              <Route path="leads" element={<AdminLeadsPage />} />
              <Route path="pagamentos" element={<AdminPagamentosPage />} />
              <Route path="usuarios" element={<AdminUsuariosPage />} />
              <Route path="documentacao" element={<AdminDocumentacaoPage />} />
              <Route path="whatsapp" element={<AdminWhatsAppPage />} />
              <Route path="configuracoes" element={<AdminConfiguracoesPage />} />
            </Route>

            {/* KDS routes (fullscreen, no dashboard sidebar) */}
            <Route path="/kds" element={<KdsLayout />}>
              <Route index element={<KdsSelectStation />} />
              <Route path="select-station" element={<KdsSelectStation />} />
              <Route path="station/:locationId" element={<KdsStationScreen />} />
              <Route path="expo" element={<KdsExpoScreen />} />
              <Route path="dashboard" element={<KdsDashboardScreen />} />
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
        </SQLiteProvider>
      </RestaurantProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
